import { useEffect, useId, useState, type FormEvent } from "react";

import { useT, type MessageKey } from "../i18n/index.js";
import { Banner } from "../ui/Banner";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { FIELD } from "../ui/classes";
import { LegalLinks } from "../legal/LegalLinks";
import { HeroMark } from "../ui/HeroMark";
import { signInUrl } from "./useAuth";
import {
  fetchReviewAvailable,
  submitReviewLogin,
  type ReviewLoginResult,
} from "./reviewLogin";

const goHome = (): void => {
  window.location.assign("/");
};

export function SignInPage({
  returnTo,
  onSignedIn = goHome,
}: {
  readonly returnTo?: string;
  /** After a review sign-in. A full page load, so every loader sees the cookie. */
  readonly onSignedIn?: () => void;
}) {
  const t = useT();
  // The review login (dev only) shows only when the server says it is open;
  // anywhere else the route is absent and this page is unchanged.
  const [reviewAvailable, setReviewAvailable] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void fetchReviewAvailable().then((available) => {
      if (!cancelled) setReviewAvailable(available);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="relative flex min-h-full items-center justify-center p-6">
      <HeroMark />
      <Card className="w-full max-w-sm p-6">
        <h1 className="mb-2 text-xl font-semibold">{t("chatTop.signInTitle")}</h1>
        <p className="mb-2 text-sm text-ink-muted">{t("chatTop.signInBlurb")}</p>
        <p className="mb-6 text-sm text-ink-muted">{t("chatTop.signInInternal")}</p>
        <a href={signInUrl(returnTo)} className="block">
          <Button className="w-full">{t("chatTop.continueWithGitlab")}</Button>
        </a>
        {reviewAvailable ? <ReviewLoginForm onSignedIn={onSignedIn} /> : null}
      </Card>
      {/* Out of flow, inside the page's bottom padding: the card stays exactly
          where it was. */}
      <LegalLinks className="absolute inset-x-0 bottom-1 px-4" />
    </div>
  );
}

const ERROR_KEY: Readonly<Record<Exclude<ReviewLoginResult, "ok">, MessageKey>> = {
  wrong: "chatTop.reviewWrong",
  too_many: "chatTop.reviewTooMany",
  closed: "chatTop.reviewClosed",
  failed: "chatTop.reviewFailed",
};

function ReviewLoginForm({ onSignedIn }: { readonly onSignedIn: () => void }) {
  const t = useT();
  const usernameId = useId();
  const passwordId = useId();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<MessageKey | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (sending) return;
    setSending(true);
    setError(null);
    const result = await submitReviewLogin(username, password);
    if (result === "ok") {
      onSignedIn();
      return;
    }
    setSending(false);
    setError(ERROR_KEY[result]);
  };

  return (
    <div className="mt-6 border-t border-line pt-6">
      <h2 className="mb-1 text-sm font-semibold">{t("chatTop.reviewHeading")}</h2>
      <p className="mb-4 text-sm text-ink-muted">{t("chatTop.reviewHint")}</p>
      <form onSubmit={(event) => void submit(event)}>
        <label htmlFor={usernameId} className="mb-1 block text-sm">
          {t("chatTop.reviewUsername")}
        </label>
        <input
          id={usernameId}
          name="username"
          type="text"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          className={`mb-3 w-full ${FIELD}`}
        />
        <label htmlFor={passwordId} className="mb-1 block text-sm">
          {t("chatTop.reviewPassword")}
        </label>
        <input
          id={passwordId}
          name="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className={`mb-4 w-full ${FIELD}`}
        />
        {error ? (
          <Banner tone="error" className="mb-4">
            {t(error)}
          </Banner>
        ) : null}
        <Button
          type="submit"
          variant="secondary"
          className="w-full"
          disabled={sending || username === "" || password === ""}
        >
          {t("chatTop.reviewSubmit")}
        </Button>
      </form>
    </div>
  );
}
